import { RefObject, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { ReviewCellRef, ReviewItem } from 'store/useReviewStore';
import { useReviewStore } from 'store/useReviewStore';
import { reviewItemId } from '../../utilities/reviewAggregator';
import { reviewCellSelector } from '../../utilities/reviewCellSelector';

const AFTER_BOX = 'content:"";position:absolute;inset:0;pointer-events:none;z-index:2;';
const HOVER_RING = `${AFTER_BOX}box-shadow:0 0 1px 1px #a614ff;display:flex;width:100%;`;
const SELECTED_RING = `${AFTER_BOX}box-shadow:0 0 0 1px #a614ff, 0 0 0 1px rgba(166, 20, 255, 0.25);display:flex;width:100%;`;
const BULK_RING = `${AFTER_BOX}box-shadow:0 0 0 1px #a614ff;`;
const SELECTED_SCROLL_MARGIN = 'scroll-margin-bottom:var(--app-draggable-drawer-height, 40px);';

// The purple review ring is for PENDING items only: a ref whose matching review
// item is settled (accepted/dismissed) resolves to null - no outline, no scroll.
// A ref with NO matching item still outlines (doc-jump before aggregation).
const unsettledRef = (ref: ReviewCellRef | null, items: ReviewItem[]): ReviewCellRef | null => {
  if (!ref) {
    return null;
  }
  const item = items.find(i => i.id === reviewItemId(ref.securityId, ref.fieldAlias));
  return item && item.status !== 'pending' ? null : ref;
};

const pendingBulkRefs = (selectedIds: Set<string>, items: ReviewItem[]): ReviewCellRef[] => {
  const byId = new Map(items.map(item => [item.id, item]));
  const refs: ReviewCellRef[] = [];
  selectedIds.forEach(id => {
    const item = byId.get(id);
    if (item && item.status === 'pending') {
      refs.push({ securityId: item.securityId, fieldAlias: item.fieldAlias });
    }
  });
  return refs;
};

const cssRule = (selectors: string[], body: string): string =>
  selectors.length === 0 ? '' : `${selectors.join(',')}{${body}}`;

// Attribute selectors (not td class names) so a React className rewrite cannot
// drop the ring. Rule order is bulk -> hover -> selected so the active card wins.
const buildReviewOutlineCss = (
  hovered: ReviewCellRef | null,
  selected: ReviewCellRef | null,
  bulk: ReviewCellRef[]
): string =>
  [
    cssRule(
      bulk.map(ref => `${reviewCellSelector(ref)}::after`),
      BULK_RING
    ),
    hovered ? cssRule([`${reviewCellSelector(hovered)}::after`], HOVER_RING) : '',
    selected ? cssRule([reviewCellSelector(selected)], SELECTED_SCROLL_MARGIN) : '',
    selected ? cssRule([`${reviewCellSelector(selected)}::after`], SELECTED_RING) : '',
  ].join('');

interface ReviewCellOutlineProps {
  /**
   * Ref to an element inside the cap-table grid. Its ownerDocument is where the
   * grid cells live; the outline stylesheet is portaled there. The grid always stays
   * in the MAIN window even when the review panel is popped out into another
   * window, so we deliberately resolve the document from the GRID, never the card.
   */
  gridRef?: RefObject<HTMLElement | null>;
}

/**
 * Mirrors review hover / active / bulk selection onto grid cells via a stylesheet
 * keyed on the cells' data attributes. Hover outlines only; the active card also
 * smooth-scrolls into view; bulk cards get a thinner ring and never scroll.
 * scroll-margin-bottom on the selected cell parks it above the bottom drawer.
 */
const ReviewCellOutline = ({ gridRef }: ReviewCellOutlineProps) => {
  const hoveredReviewCell = useReviewStore(s => unsettledRef(s.hoveredReviewCell, s.reviewItems));
  const selectedReviewCell = useReviewStore(s => unsettledRef(s.selectedReviewCell, s.reviewItems));
  const selectedCardIds = useReviewStore(s => s.selectedCardIds);
  const reviewItems = useReviewStore(s => s.reviewItems);
  const bulkRefs = useMemo(() => pendingBulkRefs(selectedCardIds, reviewItems), [selectedCardIds, reviewItems]);

  // Resolve the grid document in a passive effect so sibling refs in the same
  // commit (Outline before the table) have already attached. No stylesheet until
  // then, so we never paint a ring on the wrong document.
  const [doc, setDoc] = useState<Document | null>(null);
  useEffect(() => {
    setDoc(gridRef?.current?.ownerDocument ?? document);
  }, [gridRef]);

  // SELECTED: smooth-scroll into view. Depends ONLY on the selection (not hover
  // or bulk), so a hover sweep cannot re-fire this. rAF lets the stylesheet paint
  // first; `nearest` no-ops when the cell is already fully visible. The pending
  // frame is cancelled on cleanup so a rapid re-selection cannot land a stale
  // scroll on the previous cell.
  useEffect(() => {
    if (!selectedReviewCell || !doc) {
      return undefined;
    }
    const selectedEl = doc.querySelector(reviewCellSelector(selectedReviewCell));
    const rafId = selectedEl
      ? requestAnimationFrame(() => {
        selectedEl.scrollIntoView({ block: 'nearest', inline: 'nearest', behavior: 'smooth' });
      })
      : 0;
    return () => {
      cancelAnimationFrame(rafId);
    };
  }, [selectedReviewCell, doc]);

  const css = buildReviewOutlineCss(hoveredReviewCell, selectedReviewCell, bulkRefs);
  if (!css || !doc) {
    return null;
  }
  const mountNode = doc.head ?? doc.body;
  return createPortal(<style data-testid="review-cell-outline">{css}</style>, mountNode);
};

export default ReviewCellOutline;
